import React from 'react'

import {createPortal} from 'react-dom'

import {
  EventMarkerStackingIdCreator,
  isContainedEvent,
  markEvent,
} from '../services/dom-event-marker'
import {hasPopoverSupport} from '../services/env'

import useClickOutside from './use-click-outside'
import useHasMounted from './use-has-mounted'
import useTrackRef from './use-track-ref'

export type UsePopoverProps = {
  /** Popover state can be controlled from outside or left uncontrolled. If isOpen is set to undefined an internal state will handle opening and closing popups automatically. */
  isOpen?: boolean
  onOpenChange?: (isOpen: boolean) => void
}

export type UsePopoverReturnProps<T extends HTMLElement> = {
  ref: React.RefCallback<T | null>
  refObject: React.RefObject<T | null>
  renderPopover: (element: React.ReactElement) => React.ReactElement
  isOpen: boolean
  openPopover: () => void
  closePopover: () => void
  togglePopover: () => void
}

const usePopover = <T extends HTMLElement>({
  isOpen,
  onOpenChange,
}: UsePopoverProps = {}): UsePopoverReturnProps<T> => {
  const [innerIsOpen, setInnerIsOpen] = React.useState<boolean>(false)

  const onOpenChangeRef = React.useRef<UsePopoverProps['onOpenChange']>(null)
  onOpenChangeRef.current = onOpenChange

  const isControlledRef = React.useRef<boolean>(false)
  isControlledRef.current = isOpen !== undefined

  const innerIsOpenRef = React.useRef<boolean>(innerIsOpen)
  innerIsOpenRef.current = innerIsOpen

  const refObject = React.useRef<T>(null)

  const ref = useTrackRef<T>(element => {
    refObject.current = element

    if (hasPopoverSupport && !element.hasAttribute('popover')) {
      element.setAttribute('popover', 'manual')
      element.setAttribute('data-popover', '')

      return () => {
        element.removeAttribute('popover')
        element.removeAttribute('data-popover')
      }
    }

    if (!hasPopoverSupport && !element.hasAttribute('data-popover-fallback')) {
      element.setAttribute('data-popover-fallback', '')

      return () => {
        element.removeAttribute('data-popover-fallback')
      }
    }

    return undefined
  })

  const actualIsOpen = isOpen !== undefined ? isOpen : innerIsOpen

  const hasMounted = useHasMounted()

  // Sync open state to DOM.
  React.useEffect(() => {
    if (hasPopoverSupport && refObject.current) {
      if (actualIsOpen) {
        refObject.current.showPopover()
      } else {
        refObject.current.hidePopover()
      }
    }
  }, [actualIsOpen])

  const openPopover = React.useCallback(() => {
    if (!isControlledRef.current) {
      setInnerIsOpen(true)
    }

    onOpenChangeRef.current?.(true)
  }, [])

  const closePopover = React.useCallback(() => {
    if (!isControlledRef.current) {
      setInnerIsOpen(false)
    }

    onOpenChangeRef.current?.(false)
  }, [])

  const togglePopover = React.useCallback(() => {
    if (!isControlledRef.current) {
      setInnerIsOpen(!innerIsOpenRef.current)
    }

    onOpenChangeRef.current?.(!innerIsOpenRef.current)
  }, [])

  const renderPopover = React.useCallback(
    (element: React.ReactElement) => {
      // Do not render popovers server-side because we cannot detect popover support there.
      if (!hasMounted) {
        return <React.Fragment />
      }

      if (hasPopoverSupport) {
        return element
      }

      if (actualIsOpen) {
        return createPortal(element, document.body)
      }

      return <React.Fragment />
    },
    [actualIsOpen, hasMounted]
  )

  return {
    ref,
    refObject,
    renderPopover,
    openPopover,
    closePopover,
    togglePopover,
    isOpen: isOpen === undefined ? innerIsOpen : isOpen,
  }
}

export const useClosePopoverOnOutsideClick = <
  TAnchor extends HTMLElement,
  TPopover extends HTMLElement,
>(
  anchorRef: React.RefObject<TAnchor | null>,
  popoverRef: React.RefObject<TPopover | null>,
  closePopover: () => void,
  enabled = true
) => {
  const stackingIdRef = React.useRef<number>(0)

  React.useEffect(() => {
    // Tracking the click chain is only necessary for react portals to support click outside for nested popovers.
    // With native popovers this works out of the box.
    if (!hasPopoverSupport && enabled) {
      stackingIdRef.current = EventMarkerStackingIdCreator.create()

      const markClickEvent = (event: Event) => markEvent(event, stackingIdRef.current)

      popoverRef.current?.addEventListener('click', markClickEvent)

      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        popoverRef.current?.removeEventListener('click', markClickEvent)
      }
    }

    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  useClickOutside<TAnchor | TPopover>(
    [anchorRef, popoverRef],
    event => {
      if (isContainedEvent(event, stackingIdRef.current)) {
        return
      }

      closePopover()
    },
    enabled
  )
}

export default usePopover
